import { dateRangeFilterValueToRange } from '@app/components/date-range-selector/date-range-filter-value-to-range';
import { parseAsDateRangeFilter } from '@app/components/date-range-selector/parse-as-date-range-filter';
import { GenericDateRangeFilter } from '@app/core/list-view/base-filters/GenericDateRangeFilter';
import { GenericSelectFilter } from '@app/core/list-view/base-filters/GenericSelectFilter';
import { GenericTextFilter } from '@app/core/list-view/base-filters/GenericTextFilter';
import { createFilter, FilterDefinition } from '@app/core/list-view/types';
import { AodRuleReportTypeEnum, AodRunTypeEnum } from '@app/modules/audit/modules/aod/aod.types';
import { parseAsString } from 'nuqs';
import { defineMessage } from 'react-intl';
import { reportNameMessages } from '../../../reports/constants';
import { aodRunStatusMessages, triggerMessages } from '../../messages';
import { AodRulesFilter } from './AodRulesFilter';

export type { FilterDefinition };

/**
 * Status options for the status filter (matches getStatusLabel keys in AodRunStatus).
 */
export const statusOptions = [
  {
    value: 'COMPLETED_WITH_EXPORT',
    message: aodRunStatusMessages.COMPLETED_WITH_EXPORT,
  },
  {
    value: 'COMPLETED_WITHOUT_EXPORT',
    message: aodRunStatusMessages.COMPLETED_WITHOUT_EXPORT,
  },
  {
    value: 'IN_PROGRESS',
    message: aodRunStatusMessages.IN_PROGRESS,
  },
  {
    value: 'FAILED',
    message: aodRunStatusMessages.FAILED,
  },
] as const;

/**
 * Report type options for rule.reportType filter.
 */
export const reportTypeOptions = [
  {
    value: AodRuleReportTypeEnum.AUDIT_GIP_STATUS,
    message: reportNameMessages.AUDIT_GIP_STATUS,
  },
  {
    value: AodRuleReportTypeEnum.AUDIT_GIP_ALTERNATIVE_REPORT,
    message: reportNameMessages.AUDIT_GIP_ALTERNATIVE_REPORT,
  },
] as const;

/**
 * Type (trigger) options for the type filter.
 */
export const triggerOptions = [
  {
    value: AodRunTypeEnum.EOW,
    message: triggerMessages.EOW,
  },
  {
    value: AodRunTypeEnum.ORDER_END,
    message: triggerMessages.ORDER_END,
  },
  {
    value: AodRunTypeEnum.MANUAL,
    message: triggerMessages.MANUAL,
  },
] as const;

/**
 * Configuration array for all AOD Runs List V2 filters.
 * Permanent: dateRange, rule, status, order. Addable: rule.reportType, type.
 * Uses default Props generic so both GenericTextFilter and GenericSelectFilter (which require extra props like options) type-check.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous array: some components need extra props / value types
export const AOD_RUNS_FILTERS_CONFIG: FilterDefinition<any, any>[] = [
  createFilter({
    name: 'dateRange',
    label: false,
    parser: parseAsDateRangeFilter.withDefault({ preset: 'last7Days' }),
    isPermanent: true,
    component: GenericDateRangeFilter,
    componentProps: {},
    toApi: (val, context) => {
      const { from, to } = dateRangeFilterValueToRange(val, {
        timezone: context?.timezone as string | undefined,
      });

      const filters: string[] = [];

      // Filter by scheduledDatetime ("Ejecutada"), the primary date shown in the list.
      if (from && to) {
        filters.push(`scheduledDatetime||$between||${from},${to}`);
      } else {
        if (from) {
          filters.push(`scheduledDatetime||$gte||${from}`);
        }

        if (to) {
          filters.push(`scheduledDatetime||$lte||${to}`);
        }
      }

      return filters;
    },
  }),
  {
    name: 'rule.id',
    label: defineMessage({
      id: 'audit.aod.runs.rule',
      defaultMessage: 'Regla',
    }),
    parser: parseAsString,
    isPermanent: true,
    component: AodRulesFilter,
  },
  {
    name: 'status',
    label: defineMessage({
      id: 'audit.aod.runs.status',
      defaultMessage: 'Estado',
    }),
    parser: parseAsString.withDefault(''),
    isPermanent: true,
    component: GenericSelectFilter,
    componentProps: {
      options: statusOptions,
      sx: {
        minWidth: '20ch',
      },
    },
  },
  {
    name: 'order||$contL',
    label: defineMessage({
      id: 'audit.aod.runs.order',
      defaultMessage: 'Orden',
    }),
    parser: parseAsString.withDefault(''),
    isPermanent: true,
    component: GenericTextFilter,
  },
  {
    name: 'rule.reportType',
    label: defineMessage({
      id: 'audit.aod.runs.reportType',
      defaultMessage: 'Tipo de reporte',
    }),
    parser: parseAsString,
    isPermanent: false,
    component: GenericSelectFilter,
    componentProps: {
      options: reportTypeOptions,
      sx: {
        minWidth: '20ch',
      },
    },
  },
  {
    name: 'type',
    label: defineMessage({
      id: 'audit.aod.runs.type',
      defaultMessage: 'Trigger',
    }),
    parser: parseAsString,
    isPermanent: false,
    component: GenericSelectFilter,
    componentProps: {
      options: triggerOptions,
    },
  },
];
